import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/instrumented-toast";
import { useApiInstance } from "@/providers/api-provider";
import { AlertTriangle } from "lucide-react";
import React from "react";

interface DeleteOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  onSuccess?: () => void;
}

interface ValidationResult {
  can_delete: boolean;
  active_users: number;
  active_licenses: number;
  message: string;
}

export function DeleteOrganizationDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  onSuccess,
}: DeleteOrganizationDialogProps) {
  const [confirmText, setConfirmText] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [validation, setValidation] = React.useState<ValidationResult | null>(null);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  React.useEffect(() => {
    if (open && organizationId) {
      validateDeletion();
    } else {
      setValidation(null);
      setShowConfirmation(false);
      setConfirmText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId]);

  const validateDeletion = async () => {
    setIsValidating(true);
    try {
      const response = await useApiInstance.get(`/api/v1/organizations/${organizationId}/validate-delete`);
      setValidation(response.data);
      if (response.data.can_delete) {
        setShowConfirmation(true);
      }
    } catch (error) {
      toast.error(`Failed to validate deletion: ${error instanceof Error ? error.message : "Unknown error"}`);
      onOpenChange(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== organizationName) {
      toast.error("Organization name does not match");
      return;
    }

    setIsDeleting(true);
    try {
      await useApiInstance.delete(`/api/v1/organizations/${organizationId}/delete`);
      toast.success(`Organization "${organizationName}" deleted successfully`);
      setConfirmText("");
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(`Failed to delete organization: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting && !isValidating) {
      setConfirmText("");
      setValidation(null);
      setShowConfirmation(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Delete Organization</DialogTitle>
          <DialogDescription>
            {isValidating
              ? "Validating organization..."
              : showConfirmation
                ? "This action cannot be undone"
                : "Checking for active users and licenses"}
          </DialogDescription>
        </DialogHeader>

        {isValidating ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Validating organization...</div>
          </div>
        ) : validation && !validation.can_delete ? (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 rounded-md bg-destructive/10 p-4">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-destructive">Cannot Delete Organization</p>
                <p className="text-sm text-muted-foreground">
                  This organization has <strong>{validation.active_users} active user(s)</strong> and{" "}
                  <strong>{validation.active_licenses} active license(s)</strong>.
                </p>
                <p className="text-sm text-muted-foreground">
                  Please remove all users and licenses before deleting the organization.
                </p>
              </div>
            </div>
          </div>
        ) : showConfirmation ? (
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 rounded-md bg-yellow-50 p-4 dark:bg-yellow-950">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium">This action cannot be undone</p>
                <p className="text-sm text-muted-foreground">
                  You are about to permanently delete the organization <strong>{organizationName}</strong>.
                </p>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-text">
                Type <strong>{organizationName}</strong> to confirm
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Enter organization name..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && confirmText === organizationName && !isDeleting) {
                    handleDelete();
                  }
                }}
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          {validation && !validation.can_delete ? (
            <Button onClick={handleClose}>Close</Button>
          ) : showConfirmation ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting || confirmText !== organizationName}
              >
                {isDeleting ? "Deleting..." : "Delete Organization"}
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
